import { watch } from 'vue';

const PANEL_PATTERNS = [
  {
    prefix: '__PANEL__:conversation:',
    type: 'conversation',
    toolName: 'show_conversation',
    argKey: 'conversation_id',
  },
  {
    prefix: '__PANEL__:event:',
    type: 'event',
    toolName: 'show_event_sheet',
    argKey: 'event_id',
  },
  {
    prefix: '__PANEL__:pricing_plan:',
    type: 'pricing_plan',
    toolNames: ['pricing.show_pricing_plan', 'show_pricing_plan'],
    argKey: 'pricing_plan_id',
  },
  {
    prefix: '__PANEL__:quote:',
    type: 'quote',
    toolNames: ['planning.show_quote_ui', 'show_quote_ui'],
    argKey: 'quote_id',
  },
  {
    prefix: '__PANEL__:booking_widget:',
    type: 'booking_widget',
    toolNames: ['booking_widget_show_ui', 'booking_widget.show_ui'],
    argKey: 'booking_widget_id',
  },
];

const SHOW_EVENT_TOOLS = new Set([
  'show_event_sheet',
  'planning.show_event_ui',
  'show_event_ui',
  'planning.show_event_card',
  'show_event_card',
]);

const CREATE_EVENT_TOOLS = new Set([
  'planning.create_event',
  'create_event',
  'create_venue_inquiry',
]);

const EVENT_UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function unwrapJsonString(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('"') || !trimmed.endsWith('"')) return value;
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === 'string' ? parsed : value;
  } catch {
    return value;
  }
}

function textFromContentBlocks(blocks) {
  if (!Array.isArray(blocks)) return '';
  const textBlock = blocks.find(
    b => b && (b.type === 'text' || typeof b.text === 'string')
  );
  return typeof textBlock?.text === 'string' ? textBlock.text : '';
}

/**
 * Normalize LangGraph / MCP tool payloads into a plain string.
 * MCP json_response wraps strings as JSON; adapters may nest content blocks.
 */
function extractToolOutput(tc) {
  const payload = tc?.output ?? tc?.result;
  let raw = '';

  if (typeof payload === 'string') {
    raw = payload;
  } else if (Array.isArray(payload)) {
    raw = textFromContentBlocks(payload);
  } else if (payload && typeof payload === 'object') {
    if (typeof payload.content === 'string') {
      raw = payload.content;
    } else if (Array.isArray(payload.content)) {
      raw = textFromContentBlocks(payload.content);
    } else if (typeof payload.text === 'string') {
      raw = payload.text;
    } else {
      raw = '';
    }
  }

  return unwrapJsonString(raw || '');
}

function toolArgs(tc) {
  return tc?.args || tc?.input || tc?.arguments || {};
}

function patternToolNames(pattern) {
  if (Array.isArray(pattern.toolNames)) return pattern.toolNames;
  if (pattern.toolName) return [pattern.toolName];
  return [];
}

function tryParseReport(content) {
  try {
    const reportData = JSON.parse(content.replace('__REPORT_HTML__:', ''));
    reportData.generated_at = new Date().toISOString();
    return reportData;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[copilot] Failed to parse report data', e);
    return null;
  }
}

function tryParseJson(value) {
  if (!value || typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function extractEventId(result, args) {
  const fromArgs = args?.event_id;
  if (fromArgs && EVENT_UUID_RE.test(String(fromArgs))) {
    return String(fromArgs);
  }

  if (typeof result === 'string' && result.startsWith('__PANEL__:event:')) {
    return result.replace('__PANEL__:event:', '');
  }

  const parsed = tryParseJson(result);
  if (parsed?.id && EVENT_UUID_RE.test(String(parsed.id))) {
    return String(parsed.id);
  }

  const match = String(result || '').match(EVENT_UUID_RE);
  return match?.[0] || null;
}

function extractEventName(result, args) {
  const parsed = tryParseJson(result);
  const name = parsed?.name || args?.name;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

function resolvePanelId(result, pattern, args) {
  if (typeof result === 'string' && result.startsWith(pattern.prefix)) {
    return result.slice(pattern.prefix.length);
  }
  const fromArgs = args?.[pattern.argKey];
  return fromArgs != null && fromArgs !== '' ? String(fromArgs) : null;
}

function matchPanel(result, tc) {
  const args = toolArgs(tc);

  if (SHOW_EVENT_TOOLS.has(tc.name) || CREATE_EVENT_TOOLS.has(tc.name)) {
    const id = extractEventId(result, args);
    if (!id) return null;
    const title = extractEventName(result, args);
    return title ? { type: 'event', id, title } : { type: 'event', id };
  }

  const matched = PANEL_PATTERNS.find(
    p =>
      (typeof result === 'string' && result.startsWith(p.prefix)) ||
      patternToolNames(p).includes(tc.name)
  );
  if (matched) {
    const id = resolvePanelId(result, matched, args);
    if (!id) return null;
    return { type: matched.type, id };
  }

  if (
    (typeof result === 'string' && result.startsWith('__REPORT_HTML__:')) ||
    tc.name === 'generate_html_report'
  ) {
    const reportData = tryParseReport(result);
    if (reportData) {
      return { type: 'report', id: String(Date.now()), data: reportData };
    }
  }

  return null;
}

function messageContentToString(content) {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) return textFromContentBlocks(content);
  if (content && typeof content === 'object') {
    if (typeof content.content === 'string') return content.content;
    if (Array.isArray(content.content)) {
      return textFromContentBlocks(content.content);
    }
    if (typeof content.text === 'string') return content.text;
  }
  return '';
}

function matchPanelFromContent(content) {
  const text = unwrapJsonString(messageContentToString(content));
  const matched = PANEL_PATTERNS.find(p => text.startsWith(p.prefix));
  if (matched) {
    const id = text.slice(matched.prefix.length);
    if (!id) return null;
    return { type: matched.type, id };
  }

  if (text.startsWith('__REPORT_HTML__:')) {
    const reportData = tryParseReport(text);
    if (reportData) {
      return { type: 'report', id: String(Date.now()), data: reportData };
    }
  }

  return null;
}

/**
 * Watches tool calls and messages for panel-open triggers, invoking onOpenPanel callback.
 */
export function useCopilotPanels({ toolCalls, stream, onOpenPanel }) {
  if (!onOpenPanel) return;

  const processedIds = new Set();

  // Watch finished tool calls for panel triggers
  watch(
    toolCalls,
    newCalls => {
      (newCalls || []).forEach(tc => {
        if (tc.status !== 'finished') return;
        const tcId = tc.callId || tc.id;
        if (processedIds.has(tcId)) return;

        const result = extractToolOutput(tc);
        const panel = matchPanel(result, tc);
        if (panel) {
          processedIds.add(tcId);
          onOpenPanel(panel);
        }
      });
    },
    { deep: true }
  );

  // Fallback: detect panel commands from ToolMessages in message stream
  watch(
    () => stream.messages.value,
    newMsgs => {
      (newMsgs || []).forEach(msg => {
        // eslint-disable-next-line no-underscore-dangle
        const isToolMsg = msg.type === 'tool' || msg._getType?.() === 'tool';
        if (!isToolMsg) return;

        const msgId = msg.id || msg.tool_call_id;
        if (!msgId || processedIds.has(msgId)) return;

        const panel = matchPanelFromContent(msg.content);
        if (panel) {
          processedIds.add(msgId);
          onOpenPanel(panel);
        }
      });
    },
    { deep: true }
  );
}
